// ---------------------------------------------------------------------------
// DQ rule governance, as the Rules tab explains it. Mirrors the server's
// four-eyes check (dq_rules/governance.py four_eyes_conflicts), which is what
// decides: an approver may not be the rule's creator, the person who submitted
// this version, or anyone who changed its logic since the last approval; an
// override is confirmed by someone other than the admin who gave it.
// Pure functions, no React: unit-tested directly.
// ---------------------------------------------------------------------------

import type { Rule } from "../api/rules";

export const OVERRIDE_MIN_JUSTIFICATION = 30;

/** Why `userId` may not approve or reject `rule` (empty: they may). */
export function fourEyesConflicts(
  rule: Rule,
  userId: string | null | undefined,
): string[] {
  const g = rule.governance;
  if (!g || !userId) return [];
  const reasons: string[] = [];
  if (g.created_by && g.created_by === userId) reasons.push("you created it");
  if (g.submitted_by && g.submitted_by === userId)
    reasons.push("you submitted it");
  if (g.logic_editors.includes(userId) && g.created_by !== userId) {
    reasons.push("you changed its logic since it was last approved");
  }
  if (needsConfirmation(rule) && g.approved_by === userId) {
    // Same rule as the server: confirming an override or a pre-governance approval needs a second person.
    reasons.push("you gave the approval being confirmed");
  }
  return reasons;
}

export function fourEyesReason(
  reasons: string[],
  isPlatformAdmin = false,
): string | null {
  if (reasons.length === 0) return null;
  const next = isPlatformAdmin
    ? "As a platform administrator you may use Approve by override, with a written justification."
    : "Another Data Steward (or a platform administrator) must review it.";
  return `Four-eyes: you cannot review this rule because ${reasons.join(" and ")}. ${next}`;
}

/** An approved rule whose approval a second person should confirm (removes its marker). */
export function needsConfirmation(rule: Rule): boolean {
  const kind = rule.governance?.approval_kind;
  return (
    rule.status === "approved" &&
    (kind === "override" || kind === "pre_governance")
  );
}

/** Statuses a platform admin may approve by override from. */
export function canOverride(rule: Rule): boolean {
  return ["draft", "validated", "submitted", "rejected"].includes(rule.status);
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}
